<template>
  <d-container
    fluid
    class="main-content-container px-4">
    <!-- Page Header -->
    <d-row
      no-gutters
      class="page-header py-4">
      <d-col
        col
        class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">Zamówienia</span>
        <h3 class="page-title">Lista usuniętych zamówień</h3>
      </d-col>
    </d-row>
    <d-alert
      v-if="load_error"
      show
      theme="danger"
      class="mt-4">
      Wczytywanie danych nie powiodło się.
      <a
        href="javascript:window.location.reload();"
        class="alert-link">
        Odśwież stronę
      </a>
      aby spróbować ponownie.
    </d-alert>
    <template v-else>
      <validation-observer
        ref="orders-removed-filters-form"
        tag="form"
        class="w-100">
        <d-row class="mb-3 page-filters">
          <d-col
            col
            sm="4"
            class="mb-3">
            <d-input
              placeholder="Szukana fraza"
              v-model="filters.search"
              :disabled="!allDataLoaded" />
          </d-col>
          <d-col
            col
            sm="4"
            class="mb-3">
            <v-select
              class="w-100"
              :options="suppliers"
              :reduce="supplier => supplier.id"
              v-model="filters.supplier_id"
              placeholder="Wybierz dostawcę"
              :searchable="false"
              label="name"
              :disabled="!allDataLoaded">
              <template
                slot="option"
                slot-scope="option">
                <div class="d-center">
                  {{ option.name ? option.name : '' }}
                </div>
              </template>
              <template
                slot="selected-option"
                slot-scope="option">
                <div class="selected d-center">
                  {{ option.name ? option.name : '' }}
                </div>
              </template>
            </v-select>
          </d-col>
          <d-col
            col
            sm="4"
            class="mb-3">
            <v-select
              class="w-100"
              :options="isWeightValues"
              :reduce="weight => weight.value"
              v-model="filters.weighting_status"
              placeholder="Zważone"
              :searchable="false"
              label="name"
              :disabled="!allDataLoaded">
              <template
                slot="option"
                slot-scope="option">
                <div class="d-center">
                  {{ option.name ? option.name : '' }}
                </div>
              </template>
              <template
                lot="selected-option"
                slot-scope="option">
                <div class="selected d-center">
                  {{ option.name ? option.name : '' }}
                </div>
              </template>
            </v-select>
          </d-col>
          <d-col
            col
            sm="4"
            class="mb-3">
            <validation-provider
              :rules="filters.delivery_date_to ? 'required' : ''"
              name="delivery_date_from"
              v-slot="{ errors }">
              <date-picker
                valueType="format"
                type="date"
                class="w-100"
                :class="{'invalid-date': errors.length }"
                v-model="filters.delivery_date_from"
                :state="errors.length ? 'invalid' : null"
                :placeholder="'Data od'"
                :disabled="!allDataLoaded" />
            </validation-provider>
          </d-col>
          <d-col
            col
            sm="4"
            class="mb-3">
            <validation-provider
              :rules="filters.delivery_date_from ? 'required' : ''"
              name="delivery_date_to"
              v-slot="{ errors }">
              <date-picker
                valueType="format"
                class="w-100"
                type="date"
                :class="{'invalid-date': errors.length }"
                v-model="filters.delivery_date_to"
                :placeholder="'Data do'"
                :disabled="!allDataLoaded" />
            </validation-provider>
          </d-col>
          <d-col
            col
            sm="4"
            class="d-flex align-items-center justify-content-end mb-3"
            style="gap: 1rem;">
            <d-button
              theme="info"
              outline
              class="text-nowrap"
              :disabled="!allDataLoaded"
              @click.prevent="validateFiltersForm">Szukaj</d-button>
            <d-button
              theme="danger"
              outline
              class="text-nowrap"
              :disabled="!allDataLoaded"
              @click.prevent="clearFilters">Resetuj filtry</d-button>
          </d-col>
        </d-row>
      </validation-observer>

      <d-card class="w-100 mb-5">
        <table class="table table-striped mt-0 mb-0 full-width">
          <thead class="bg-light">
            <tr>
              <th
                scope="col"
                class="border-0">Klient</th>
              <th
                scope="col"
                class="border-0">Placówka</th>
              <th
                scope="col"
                class="border-0 text-center">Data utworzenia</th>
              <th
                scope="col"
                class="border-0 text-center">Data usunięcia</th>
              <th
                scope="col"
                class="border-0 text-right">Produkty</th>
              <th
                scope="col"
                class="border-0 text-center"
                style="width: 80px;">Akcje</th>
            </tr>
          </thead>
          <tbody v-if="allDataLoaded && orders">
            <tr
              v-for="(order) of orders"
              :key="'order-' + order.id">
              <td class="align-middle">
                {{ order.customer_name || '-' }}
              </td>
              <td class="align-middle">
                {{ order.shop_name || '-' }}
                <span
                  v-if="order.shop_address || order.shop_post_code || order.shop_city"
                  class="bottom-text">
                  <span class="bottom-text-value">
                    <template v-if="order.shop_post_code || order.shop_city">
                      <span class="text-secondary">Adres: </span>
                      <span class="text-success">{{order.shop_post_code + ' ' + order.shop_city}}</span><br>
                    </template>
                    <template v-if="order.shop_address">
                      <span class="text-secondary">Ulica: </span>
                      <span class="text-success">{{ order.shop_address }}</span>
                    </template>
                  </span>
                </span>
              </td>
              <td class="align-middle text-center">
                {{ formatDate(order.created_at) }}
              </td>
              <td class="align-middle text-center">
                {{ formatDate(order.deleted_at) }}
              </td>
              <td class="align-middle text-right">
                {{ order.products_count }}
              </td>
              <td class="align-middle text-center">
                <d-button
                  title="Przywróć"
                  theme="warning"
                  :class="{
                    'btn-icon': true,
                    'isLoading': isRestoreInProgress === order.id
                  }"
                  @click="restoreOrder(order.id)"
                  :disabled="!!isRestoreInProgress">
                  <i class="material-icons">settings_backup_restore</i>
                </d-button>
              </td>
            </tr>
            <tr v-if="orders.length === 0">
              <td
                colspan="8"
                class="text-center">
                Brak usuniętych zamówień do wyświetlenia&hellip;
              </td>
            </tr>
          </tbody>
        </table>

        <div
          v-if="!allDataLoaded"
          class="pb-3 pt-3 text-center overlay-loading">
          Trwa ładowanie danych&hellip;
        </div>
      </d-card>

      <pagination
        :meta-data="paginationMetaData"
        :initial-page="currentPage"/>
    </template>
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import FormUtils from '@/utils/FormUtils.js';
import ListUtils from '@/utils/ListUtils.js';
import CalendarUtils from '@/utils/CalendarUtils.js';
import HasFilters from '@/mixins/HasFilters.vue';
import HasPagination from '@/mixins/HasPagination.vue';
import Pagination from '../../components/common/Pagination.vue';
import DatePicker from 'vue2-datepicker';

export default {
  name: 'orders-removed',
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    DatePicker,
    'pagination': Pagination
  },
  data () {
    return {
      filters: {
        delivery_date_from: null,
        delivery_date_to: null,
        search: null,
        supplier_id: null,
        weighting_status: null
      },
      isRestoreInProgress: false,
      isWeightValues: [{
        name: 'Wszystkie',
        value: null
      }, {
        name: 'Tylko niezważone',
        value: 'UNWEIGHT'
      }, {
        name: 'Tylko częściowo zważone',
        value: 'PARTIAL'
      }, {
        name: 'Tylko zważone',
        value: 'FULL'
      }],
      load_error: false,
      loaded: false,
      loaded_suppliers: false,
      min_date_from: null,
      orders: [],
      suppliers: []
    };
  },
  computed: {
    allDataLoaded () {
      return this.loaded && this.loaded_suppliers;
    }
  },
  mounted () {
    this.loadSuppliers();
    this.loadFilteredData();
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = {
        delivery_date_from: this.filters.delivery_date_from ? this.filters.delivery_date_from : '',
        delivery_date_to: this.filters.delivery_date_to ? this.filters.delivery_date_to : '',
        only_trashed: 1,
        search: this.filters.search,
        supplier_id: this.filters.supplier_id ? this.filters.supplier_id : '',
        weighting_status: this.filters.weighting_status
      };

      ListUtils.loadItemsData(this, {
        endpoint: '/orders/items?page=' + this.currentPage,
        listField: 'orders',
        errorKey: 'load_error',
        pagination: true,
        params: {
          where
        }
      });
    },
    loadSuppliers () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/suppliers/items',
        loadedKey: 'loaded_suppliers',
        outputKey: 'suppliers',
        errorKey: 'load_error',
        params: {
          pagination: false,
          where: { active: 1 }
        },
      });
    },
    restoreOrder (orderId) {
      this.isRestoreInProgress = true;
      FormUtils.submit(this, {
        endpoint: 'orders/restore/' + parseInt(orderId, 10),
        successTitle: 'Przywrócono zamówienie',
        successText: 'Przywrócenie zamówienia pomyślnie zakończone.',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Przywrócenie zamówienia nie powiodła się. Spróbuj ponownie.',
        successAction: () => {
          this.loaded = false;
          this.isRestoreInProgress = false;
          this.loadFilteredData();
        },
        failAction: () => {
          this.isRestoreInProgress = false;
        }
      });
    },
    clearFilters () {
      this.loaded = false;
      this.load_error = false;
      this.filters = {
        delivery_date_from: null,
        delivery_date_to: null,
        search: null,
        supplier_id: null,
        weighting_status: null
      };

      this.loadFilteredData();
      this.$bus.$emit('view-filters-reset');
    },
    formatDate (date) {
      return CalendarUtils.formatDateForPHP(new Date(date));
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['orders-removed-filters-form'], this.debouncedLoadFilteredData);
    }
  }
};
</script>
